import { useMemo } from 'react';
import { parseISO, format } from 'date-fns';
import { findPercentileDate, cumulativeProbabilityByDate } from '@/lib/utils';
import type { ProcessedDeliverableResult, DependencyMap, DateFrequency } from '@/types/api';
import type {
  DeliverableTimelineData,
  GroupedData,
  GroupMode,
  AggregateTimelineData,
  GroupTimeline,
  TimelineGap,
} from '@/types/timeline';

/** Group name for deliverables not assigned to any feature */
export const UNASSIGNED_GROUP_NAME = 'Unassigned to Feature';

interface UseTimelineDataOptions {
  deliverables: ProcessedDeliverableResult[];
  groupMode: GroupMode;
  /** Feature names in configuration order (from simulation setup). Used to sort feature groups. */
  featureOrder: string[];
  /** Optional dependency map to enrich timeline data with predecessor/dependent relationships */
  dependencyMap?: DependencyMap;
  /** Feature due dates (feature name → due date) for displaying on group headers */
  featureDueDates?: Map<string, Date | null>;
}

interface UseTimelineDataResult {
  /** Processed timeline data for each deliverable */
  timelineData: DeliverableTimelineData[];
  /** Unique team names in order of appearance */
  teams: string[];
  /** Data grouped by team or feature */
  groupedData: GroupedData[];
  /** Flattened data for date range calculations */
  flatData: DeliverableTimelineData[];
}

/**
 * Calculate aggregate timeline from a group of deliverables
 *
 * For features: the aggregate represents the full span of work.
 * - Start dates use MIN (feature starts when first deliverable starts)
 * - Finish dates use MAX (feature is done when last deliverable finishes)
 * - Due date shows the earliest (most constraining) deadline
 */
function calculateAggregateTimeline(groupName: string, items: DeliverableTimelineData[]): AggregateTimelineData {
  // Helper to find min date (earliest)
  const minDate = (dates: (Date | null)[]): Date | null => {
    const valid = dates.filter((d): d is Date => d !== null);
    if (valid.length === 0) return null;
    return new Date(Math.min(...valid.map((d) => d.getTime())));
  };

  // Helper to find max date (latest)
  const maxDate = (dates: (Date | null)[]): Date | null => {
    const valid = dates.filter((d): d is Date => d !== null);
    if (valid.length === 0) return null;
    return new Date(Math.max(...valid.map((d) => d.getTime())));
  };

  // Find earliest due date and its probability
  let earliestDueDate: Date | null = null;
  let earliestDueDateProbability: number | null = null;

  for (const item of items) {
    if (item.dueDate) {
      if (earliestDueDate === null || item.dueDate < earliestDueDate) {
        earliestDueDate = item.dueDate;
        earliestDueDateProbability = item.dueDateProbability;
      }
    }
  }

  return {
    groupName,
    deliverableCount: items.length,
    // Start = MIN (when does the feature start?)
    startP20: minDate(items.map((i) => i.startP20)),
    startP50: minDate(items.map((i) => i.startP50)),
    // Finish = MAX (when is the feature done?)
    finishP50: maxDate(items.map((i) => i.finishP50)),
    finishP70: maxDate(items.map((i) => i.finishP70)),
    finishP85: maxDate(items.map((i) => i.finishP85)),
    finishP95: maxDate(items.map((i) => i.finishP95)),
    earliestDueDate,
    earliestDueDateProbability,
  };
}

/**
 * Get exact probability of a deliverable being finished by a given date.
 *
 * Looks up the cumulative probability directly from the Monte Carlo
 * distribution via {@link cumulativeProbabilityByDate} — the same lookup the
 * distribution/spread view uses — so the timeline and the distribution agree
 * at the boundaries. In particular, a target earlier than every simulated
 * finish reads 0% (not the earliest bucket's mass), and a target on/after the
 * last finish reads ~100%.
 *
 * @param distribution - Sorted array of date frequencies from Monte Carlo simulation
 * @param targetDate - The date to look up probability for
 * @returns Cumulative probability (0-1) or null if distribution is empty
 */
export function getExactProbabilityAtDate(distribution: DateFrequency[], targetDate: Date): number | null {
  return cumulativeProbabilityByDate(distribution, format(targetDate, 'yyyy-MM-dd'));
}

/**
 * Calculate probability of ALL deliverables being finished by a given date
 *
 * For a feature to be complete, ALL its deliverables must be done.
 * P(feature done) = P(A done) × P(B done) × P(C done) × ...
 *
 * Uses exact cumulative probabilities from each deliverable's Monte Carlo distribution.
 *
 * @param items - Array of deliverables with their finish distributions
 * @param dueDate - The feature due date to calculate probability for
 * @returns Combined probability (0-1) or null if no valid probabilities
 */
export function calculateFeatureDueDateProbability(items: DeliverableTimelineData[], dueDate: Date): number | null {
  if (items.length === 0) return null;

  let combinedProbability = 1.0;
  let hasValidProbability = false;

  for (const item of items) {
    const prob = getExactProbabilityAtDate(item.finishedDistribution, dueDate);
    if (prob !== null) {
      combinedProbability *= prob;
      hasValidProbability = true;
    }
  }

  return hasValidProbability ? combinedProbability : null;
}

/**
 * Check if a deliverable has zero duration (startP50 === finishP50).
 *
 * Zero-work deliverables complete instantly in the simulation — their started
 * and finished distributions are identical. They act as gating mechanisms
 * (e.g., approvals, handoffs) rather than representing real work.
 */
export function isZeroDuration(item: DeliverableTimelineData): boolean {
  return item.startP50 !== null && item.finishP50 !== null && item.startP50.getTime() === item.finishP50.getTime();
}

/**
 * Split deliverables into contiguous work blocks and detect gaps between them
 *
 * Uses an interval-merging algorithm at the P50 level:
 * - Sort items by startP50
 * - Merge overlapping items (where next start <= current block's max finish)
 * - Record gaps between non-overlapping blocks
 *
 * Zero-duration deliverables (startP50 === finishP50) are excluded from block
 * calculation to avoid creating misleading 2px artifact bars and phantom gaps
 * in the group header aggregate view.
 *
 * Falls back to a single block if any item lacks P50 dates.
 */
function calculateGroupTimeline(
  groupName: string,
  items: DeliverableTimelineData[],
  featureDueDate?: Date | null,
): GroupTimeline {
  // Fallback: if any item lacks P50 dates, return single block (no gap detection possible)
  const itemsWithP50 = items.filter((i) => i.startP50 !== null && i.finishP50 !== null);
  if (itemsWithP50.length < items.length) {
    const aggregate = calculateAggregateTimeline(groupName, items);
    const probability = featureDueDate ? calculateFeatureDueDateProbability(items, featureDueDate) : null;
    return {
      blocks: [aggregate],
      gaps: [],
      featureDueDate,
      featureDueDateProbability: probability,
    };
  }

  // Exclude zero-duration items from block calculation — they create misleading
  // 2px artifact bars and phantom gaps in the group header aggregate view
  const workItems = itemsWithP50.filter((i) => !isZeroDuration(i));

  // If all items are zero-duration, fall back to single aggregate block
  if (workItems.length === 0) {
    const aggregate = calculateAggregateTimeline(groupName, items);
    const probability = featureDueDate ? calculateFeatureDueDateProbability(items, featureDueDate) : null;
    return {
      blocks: [aggregate],
      gaps: [],
      featureDueDate,
      featureDueDateProbability: probability,
    };
  }

  // Sort by startP50
  const sorted = [...workItems].sort((a, b) => a.startP50!.getTime() - b.startP50!.getTime());

  // Merge overlapping intervals into contiguous blocks
  const blockItems: DeliverableTimelineData[][] = [];
  let currentBlock: DeliverableTimelineData[] = [sorted[0]!];
  let maxFinish = sorted[0]!.finishP50!.getTime();

  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i]!;
    if (item.startP50!.getTime() <= maxFinish) {
      // Overlaps with current block
      currentBlock.push(item);
      maxFinish = Math.max(maxFinish, item.finishP50!.getTime());
    } else {
      // Gap found — save current block, start new one
      blockItems.push(currentBlock);
      currentBlock = [item];
      maxFinish = item.finishP50!.getTime();
    }
  }
  blockItems.push(currentBlock);

  // Build aggregate timelines for each block
  const blocks = blockItems.map((bi, idx) => calculateAggregateTimeline(`${groupName} (block ${idx + 1})`, bi));

  // Compute gaps between consecutive blocks
  const gaps: TimelineGap[] = [];
  for (let i = 0; i < blockItems.length - 1; i++) {
    const prevFinish = Math.max(...blockItems[i]!.map((it) => it.finishP50!.getTime()));
    const nextStart = blockItems[i + 1]![0]!.startP50!;
    gaps.push({
      start: new Date(prevFinish),
      end: nextStart,
    });
  }

  // Calculate feature due date probability: P(all deliverables done by date)
  // This is the product of each deliverable's probability of being done by that date
  const probability = featureDueDate ? calculateFeatureDueDateProbability(items, featureDueDate) : null;

  return { blocks, gaps, featureDueDate, featureDueDateProbability: probability };
}

/**
 * Hook to process deliverable results into timeline visualization data
 *
 * Transforms raw simulation results into structured data for timeline rendering:
 * - Calculates start and finish percentiles
 * - Determines due date probabilities
 * - Groups data by team or feature
 *
 * @example
 * ```tsx
 * const { groupedData, flatData } = useTimelineData({
 *   deliverables,
 *   groupMode: "team",
 * });
 * ```
 */
export function useTimelineData({
  deliverables,
  groupMode,
  featureOrder,
  dependencyMap,
  featureDueDates,
}: UseTimelineDataOptions): UseTimelineDataResult {
  // Process deliverable data for timeline
  const timelineData = useMemo<DeliverableTimelineData[]>(() => {
    // Track priority per team (deliverables arrive in team order from API)
    const teamPriorityCounters = new Map<string, number>();

    return deliverables.map((deliverable) => {
      // Calculate team priority (1-based index within the team)
      const currentPriority = (teamPriorityCounters.get(deliverable.team_name) ?? 0) + 1;
      teamPriorityCounters.set(deliverable.team_name, currentPriority);
      // Calculate start percentiles
      const startP20Str = findPercentileDate(deliverable.started, 0.2);
      const startP50Str = findPercentileDate(deliverable.started, 0.5);

      // Calculate finish percentiles
      const finishP50Str = findPercentileDate(deliverable.finished, 0.5);
      const finishP70Str = findPercentileDate(deliverable.finished, 0.7);
      const finishP85Str = findPercentileDate(deliverable.finished, 0.85);
      const finishP95Str = findPercentileDate(deliverable.finished, 0.95);

      // Probability of finishing by the due date — same lookup the
      // distribution view uses, so the timeline marker can't disagree with the
      // distribution at the 0% boundary (a due date before every simulated
      // finish reads 0%, not the earliest bucket's cumulative mass).
      const dueDateProbability = deliverable.due_date
        ? cumulativeProbabilityByDate(deliverable.finished, deliverable.due_date)
        : null;

      // Get dependency relationships from map (using database UUID)
      const deliverableUuid = deliverable.deliverable_id;
      const predecessorIds =
        deliverableUuid && dependencyMap?.predecessors[deliverableUuid]
          ? dependencyMap.predecessors[deliverableUuid]
          : [];
      const dependentIds =
        deliverableUuid && dependencyMap?.dependents[deliverableUuid] ? dependencyMap.dependents[deliverableUuid] : [];

      return {
        id: `${deliverable.feature_name ?? 'no-feature'}:${deliverable.team_name}:${deliverable.deliverable_name}`,
        deliverableId: deliverable.deliverable_id ?? null,
        teamName: deliverable.team_name,
        featureName: deliverable.feature_name ?? null,
        deliverableName: deliverable.deliverable_name,
        sourceId: deliverable.source_id ?? null,
        teamPriority: currentPriority,
        startP20: startP20Str ? parseISO(startP20Str) : null,
        startP50: startP50Str ? parseISO(startP50Str) : null,
        finishP50: finishP50Str ? parseISO(finishP50Str) : null,
        finishP70: finishP70Str ? parseISO(finishP70Str) : null,
        finishP85: finishP85Str ? parseISO(finishP85Str) : null,
        finishP95: finishP95Str ? parseISO(finishP95Str) : null,
        dueDate: deliverable.due_date ? parseISO(deliverable.due_date) : null,
        dueDateProbability,
        predecessorIds,
        dependentIds,
        finishedDistribution: deliverable.finished,
      };
    });
  }, [deliverables, dependencyMap]);

  // Get unique teams for ordering
  const teams = useMemo(() => {
    const teamSet = new Set<string>();
    for (const d of timelineData) {
      teamSet.add(d.teamName);
    }
    return Array.from(teamSet);
  }, [timelineData]);

  // Group data by team or feature (for visualization only - filtering is done in parent)
  const groupedData = useMemo<GroupedData[]>(() => {
    const groups = new Map<string, DeliverableTimelineData[]>();

    if (groupMode === 'team') {
      // Group by team; order already matches configuration from App
      for (const item of timelineData) {
        const existing = groups.get(item.teamName) || [];
        existing.push(item);
        groups.set(item.teamName, existing);
      }
    } else {
      // Group by feature name (useful when same feature spans multiple teams)
      // Deliverables without a feature are collected into a single "Unassigned Work" group
      for (const item of timelineData) {
        const groupKey = item.featureName ?? UNASSIGNED_GROUP_NAME;
        const existing = groups.get(groupKey) || [];
        existing.push(item);
        groups.set(groupKey, existing);
      }
      // Sort deliverables within each feature by start date
      for (const [, items] of groups) {
        items.sort((a, b) => {
          const aStart = a.startP50?.getTime() ?? 0;
          const bStart = b.startP50?.getTime() ?? 0;
          return aStart - bStart;
        });
      }
    }

    const result: GroupedData[] = [];

    if (groupMode === 'team') {
      // Preserve team order from configuration
      // Teams always show headers (no due dates for teams)
      for (const teamName of teams) {
        const items = groups.get(teamName);
        if (!items || items.length === 0) continue;
        result.push({
          groupName: teamName,
          items,
          aggregateTimeline: calculateGroupTimeline(teamName, items),
        });
      }
    } else {
      // Group by feature: always show header so due dates and aggregate timelines are visible
      for (const [groupName, items] of groups) {
        const isUnassigned = groupName === UNASSIGNED_GROUP_NAME;

        // Get feature's own due date (not available for unassigned group)
        const featureDueDate = !isUnassigned ? featureDueDates?.get(groupName) : undefined;

        result.push({
          groupName,
          items,
          aggregateTimeline: calculateGroupTimeline(groupName, items, featureDueDate),
        });
      }
      // Sort feature groups by configuration order (matching the simulation setup page)
      result.sort((a, b) => {
        const aIdx = featureOrder.indexOf(a.groupName);
        const bIdx = featureOrder.indexOf(b.groupName);
        // Features not in config order (e.g. ungrouped deliverables) go to the end
        const aOrder = aIdx === -1 ? featureOrder.length : aIdx;
        const bOrder = bIdx === -1 ? featureOrder.length : bIdx;
        return aOrder - bOrder;
      });
    }

    return result;
  }, [timelineData, groupMode, teams, featureOrder, featureDueDates]);

  // Flatten for date range calculation
  const flatData = useMemo(() => {
    return groupedData.flatMap((g) => g.items);
  }, [groupedData]);

  return { timelineData, teams, groupedData, flatData };
}
